import React from 'react';
import { translate } from 'react-i18next';
import NavbarButton from '../../shared/NavbarButton';
import CBMediaQuery from '../../shared/CBMediaQuery';
import { BLEEDING_EDGE_USERS, SERVICE_CHAIN, SERVICE_WORKSHOP, SERVICE_PERSONAL, getStyle } from '../../../utils/utils';

class VehicleDetailsNavbar extends React.Component {
  // Determine which tab is active by URL
  getActiveTab = () => {
    const { isUnpaidFleetUser } = this.props;
    const url = window.location.pathname;

    if (url.includes('/messages')) return 'Message';
    if (url.includes('/tasks')) return 'Task';
    if (url.includes('/treatments')) return 'Treatment';
    if (url.includes('/maintenances')) return 'Maintenance';
    if (url.includes('/sale_report')) return 'Sale report';
    if (url.includes('/mileage')) return 'Mileage';
    if (url.includes('/document')) return 'Document';
    if (url.includes('/reservations')) return 'Reservation';

    // Fallback
    return isUnpaidFleetUser ? 'Maintenance' : 'Basic';
  };

  buildButtonConfig = ({ vehicle, t, isUnpaidFleetUser, hasFeatureTasksAndMessages, service, user, selectedWorkshopId }) => {
    // Common checks
    const isNotPersonal = !vehicle.is_personal_vehicle;
    const isChain = service === SERVICE_CHAIN;
    const isChainWorkshop = service === SERVICE_WORKSHOP
      && user && user.workshops
      && user.workshops.some(function(w) { return w.id == selectedWorkshopId && w.belongs_to_chain; });
    const showSaleReport = vehicle.is_personal_vehicle && service === SERVICE_PERSONAL;

    return [
      {
        id: 'Basic',
        to: `/vehicle/${vehicle.id}/basic`,
        iconSrc: '/img/icon_car_basic_info.svg',
        text: t('basicInfoHeading'),
        visible: true,
        disabled: isUnpaidFleetUser,
      },
      {
        id: 'Maintenance',
        to: `/vehicle/${vehicle.id}/maintenances/`,
        iconSrc: '/img/icon_car_service.svg',
        text: t('maintenanceHistoryHeading'),
        visible: !!vehicle.maintenances || isChain,
      },
      {
        id: 'Treatment',
        to: `/vehicle/${vehicle.id}/treatments`,
        iconSrc: '/img/icon_car_maintenance.svg',
        text: t('treatmentHistoryHeading'),
        visible: !!vehicle.treatments || isChain,
      },
      {
        id: 'Sale report',
        to: `/vehicle/${vehicle.id}/sale_report`,
        iconSrc: '/img/icon_vehicle_report.svg',
        text: t('saleReportHeading'),
        visible: showSaleReport,
      },
      {
        id: 'Message',
        to: `/vehicle/${vehicle.id}/messages`,
        iconSrc: '/img/icon_announcements.svg',
        text: t('messagesHeading'),
        visible: isNotPersonal && hasFeatureTasksAndMessages,
      },
      {
        id: 'Task',
        to: `/vehicle/${vehicle.id}/tasks`,
        iconSrc: '/img/icon_task_list.svg',
        text: t('tasksHeading'),
        visible: isNotPersonal && hasFeatureTasksAndMessages,
      },
      {
        id: 'Mileage',
        to: `/vehicle/${vehicle.id}/mileages`,
        iconSrc: '/img/icon-wheel.svg',
        text: t('tasksMileage'),
        visible: isNotPersonal,
      },
      {
        id: 'Document',
        to: `/vehicle/${vehicle.id}/documents`,
        iconSrc: '/img/icon_document.svg',
        text: t('documentsHeading'),
        visible: true,
      },
      {
        id: 'Reservation',
        to: `/vehicle/${vehicle.id}/reservations`,
        iconSrc: '/img/icon_calendar.svg',
        text: t('reservationsHeading'),
        visible: isChain || isChainWorkshop,
      },
    ];
  };

  // For small screens, split visible buttons evenly across two rows
  getSmallGroups = (buttons) => {
    const visible = buttons.filter(b => b.visible);
    if (visible.length <= 4) {
      return [visible, []];
    }
    const half = Math.ceil(visible.length / 2);
    return [visible.slice(0, half), visible.slice(half)];
  };

  renderNavbarButtons = (buttonList, activeTab) => {
    // Render each visible button using NavbarButton
    return buttonList.map(btn => (
      <NavbarButton
        key={btn.id}
        to={btn.to}
        iconSrc={btn.iconSrc}
        alt={btn.alt || btn.id}
        buttonText={btn.text}
        setActiveTab={tabName => this.setState({ activeTab: tabName })}
        activeTab={activeTab}
        disabled={btn.disabled}
        column
      />
    ));
  };

  render() {
    const { t, vehicle, isUnpaidFleetUser, hasFeatureTasksAndMessages, service, user } = this.props;
    const activeTab = this.getActiveTab();

    // This var was in original code but never used for anything visible. Keeping if needed.
    const isReportDemoUser =
      user.is_staff || BLEEDING_EDGE_USERS.includes(user.email) || user.email.includes('ovio.fi');

    // 1) Build the main button array
    const allButtons = this.buildButtonConfig({
      vehicle,
      t,
      isUnpaidFleetUser,
      hasFeatureTasksAndMessages,
      service,
      user,
      selectedWorkshopId: this.props.selectedWorkshopId,
    });

    return (
      <CBMediaQuery>
        {screenSize => {
          const isSmall = screenSize === 'small';

          // 2) Determine how to display them
          let group1 = allButtons.filter(b => b.visible); // default to all
          let group2 = []; // by default, no second row

          // For small, replicate old grouping logic
          if (isSmall) {
            const [smallGroup1, smallGroup2] = this.getSmallGroups(allButtons);
            group1 = smallGroup1;
            group2 = smallGroup2;
          }

          return (
            <div style={getStyle(screenSize, styles, 'container')}>
              <div
                style={{
                  ...getStyle(screenSize, styles, 'navbarContainer'),
                  flexDirection: isSmall ? 'column' : 'row',
                }}
              >
                {/* Group 1 */}
                <div style={getStyle(screenSize, styles, 'buttonGroup')}>
                  {this.renderNavbarButtons(group1, activeTab)}
                </div>

                {/* Group 2 (only in small view, second row) */}
                {group2.length > 0 && (
                  <div
                    style={{
                      ...getStyle(screenSize, styles, 'buttonGroup'),
                      borderTop: isSmall ? '1px solid rgba(255,255,255,.3)' : 0,
                    }}
                  >
                    {this.renderNavbarButtons(group2, activeTab)}
                  </div>
                )}
              </div>
            </div>
          );
        }}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    container: {
      width: '100%',
      backgroundColor: 'rgba(0, 149, 218, 0.4)',
    },
    navbarContainer: {
      display: 'flex',
      width: '70%',
    },
    buttonGroup: {
      width: '100%',
      display: 'flex',
    },
  },
  medium: {
    navbarContainer: {
      width: '100%',
    },
  },
  // optional extra style overrides
};

export default translate('VehicleDetailsView')(VehicleDetailsNavbar);
